import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { parse } from 'best-effort-json-parser'
import { useLocalStorage } from 'usehooks-ts'

import { useConfig } from './use-config'

export type FileData = {
  name: string
  type: string
  size: number
  text: string
}

export type Step = 'upload' | 'analyze' | 'tailor' | 'result'

export interface ResumeAlphaData {
  input: {
    resume?: string
    job_description?: string
  }
  analysis: {
    ats_score?: number
    reasonings?: string[]
    improvements?: { checked: boolean; value: string; defaultValue: string }[]
    custom_request?: { enabled?: Boolean; value?: string }
  } | null
  result: {
    ats_score?: number
    resume?: string
  } | null
}

function cleanChunk(chunk: string): string {
  return chunk
    .replace(/```json\s*\n?/g, '') // Remove ```json
    .replace(/\n?```\s*/g, '') // Remove closing ```
    .trim()
}

export const useResumeTailor = () => {
  const { config } = useConfig()
  const [selectedFiles, setSelectedFiles] = useLocalStorage<FileData[]>(
    'resume-alpha-selectedFiles',
    [],
  )
  const [data, setData] = useLocalStorage<ResumeAlphaData>(
    'resume-alpha-data',
    {
      input: { resume: '', job_description: '' },
      analysis: null,
      result: null,
    },
  )

  const [step, setStep] = React.useState<Step>('upload')
  const [isStreaming, setIsStreaming] = React.useState(false)

  React.useEffect(() => {
    if (
      step === 'upload' &&
      (data?.input?.job_description || data?.analysis || data?.result)
    ) {
      setData((prev) => ({
        ...prev,
        input: { ...prev.input, job_description: '' },
        analysis: null,
        result: null,
      }))
    } else if (step === 'analyze' && data?.result) {
      setData((prev) => ({ ...prev, result: null }))
    }
  }, [step])

  const analyzeMutation = useMutation({
    mutationFn: async (opts: {
      resume_text: string
      job_description: string
    }) => {
      const formDataToSend = new FormData()
      formDataToSend.append('llm_settings', JSON.stringify(config))
      formDataToSend.append('resume_text', opts.resume_text)
      formDataToSend.append('job_description', opts.job_description)

      const res = await fetch('/api/analyze', {
        method: 'POST',
        body: formDataToSend,
      })

      if (!res.ok) throw new Error('Failed to analyze')

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let accumulatedContent = ''
      let sseBuffer = ''

      if (!reader) throw new Error('No reader available')

      while (true) {
        setIsStreaming(true)
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        sseBuffer += chunk

        // Parse SSE format: data: {...}\n\n
        const lines = sseBuffer.split('\n')
        // Keep the last potentially incomplete line in the buffer
        sseBuffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || trimmed === 'data: [DONE]') continue
          if (!trimmed.startsWith('data: ')) continue

          try {
            const jsonStr = trimmed.slice(6) // Remove 'data: ' prefix
            const event = JSON.parse(jsonStr)

            if (event.type === 'content' && event.delta) {
              accumulatedContent += event.delta
            }
          } catch (e) {
            // Skip malformed JSON lines
          }
        }

        // Try to parse accumulated content as JSON
        try {
          const partialData = parse(cleanChunk(accumulatedContent))
          if (partialData) {
            const { ats_score, reasonings, improvements } = partialData
            setData((prev) => ({
              ...prev,
              analysis: {
                ats_score: ats_score,
                reasonings: reasonings,
                improvements: improvements?.map((val: string) => ({
                  checked: true,
                  value: val,
                  defaultValue: val,
                })),
              },
            }))

            setStep('analyze')
          }
        } catch (e) {}
      }

      try {
        const data = parse(cleanChunk(accumulatedContent))
        return data
      } catch (e) {
        console.error('Failed to parse final JSON', accumulatedContent)
        throw new Error('Invalid JSON response from stream')
      }
    },
    onSuccess: (data) => {
      const { ats_score, reasonings, improvements } = data
      setData((prev) => ({
        ...prev,
        analysis: {
          ats_score: ats_score,
          reasonings: reasonings,
          improvements: improvements?.map((val: string) => ({
            checked: true,
            value: val,
            defaultValue: val,
          })),
        },
        result: null,
      }))
      setIsStreaming(false)
      setStep('analyze')
    },
  })

  const tailorMutation = useMutation({
    mutationFn: async (opts: {
      resume_text: string
      job_description: string
      ats_score: number
      reasonings: string[]
      improvements: string[]
      custom_user_request?: string
    }) => {
      const formDataToSend = new FormData()
      formDataToSend.append('llm_settings', JSON.stringify(config))
      formDataToSend.append('resume_text', opts.resume_text)
      formDataToSend.append('job_description', opts.job_description)
      formDataToSend.append('ats_score', opts.ats_score.toString())
      formDataToSend.append('reasonings', opts.reasonings.join('\n'))
      formDataToSend.append('improvements', opts.improvements.join('\n'))
      if (opts.custom_user_request)
        formDataToSend.append('custom_user_request', opts.custom_user_request)

      // Use dedicated streaming Lambda function URL in production to avoid API Gateway timeout
      // Falls back to /api/tailor for local development
      // In production, this goes through CloudFront on tailor.resume-alpha.com (same domain family)
      const tailorUrl =
        import.meta.env.VITE_TAILOR_FUNCTION_URL || '/api/tailor'

      const res = await fetch(tailorUrl, {
        method: 'POST',
        body: formDataToSend,
      })

      if (!res.ok) throw new Error('Failed to tailor')

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let accumulatedContent = ''
      let sseBuffer = ''

      if (!reader) throw new Error('No reader available')

      while (true) {
        setStep('result')
        setIsStreaming(true)
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        sseBuffer += chunk

        // Parse SSE format: data: {...}\n\n
        const lines = sseBuffer.split('\n')
        // Keep the last potentially incomplete line in the buffer
        sseBuffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || trimmed === 'data: [DONE]') continue
          if (!trimmed.startsWith('data: ')) continue

          try {
            const jsonStr = trimmed.slice(6) // Remove 'data: ' prefix
            const event = JSON.parse(jsonStr)

            if (event.type === 'content' && event.delta) {
              accumulatedContent += event.delta
            }
          } catch (e) {
            // Skip malformed JSON lines
          }
        }

        // Try to parse accumulated content as JSON
        try {
          const partialData = parse(cleanChunk(accumulatedContent))
          if (partialData) {
            setData((prev) => ({
              ...prev,
              result: {
                ats_score: partialData?.ats_score,
                resume: partialData?.tailored_resume_markdown,
              },
            }))
          }
        } catch (e) {}
      }

      try {
        const data = parse(cleanChunk(accumulatedContent))
        return data
      } catch (e) {
        console.error('Failed to parse final JSON', accumulatedContent)
        throw new Error('Invalid JSON response from stream')
      }
    },
    onSuccess: (data) => {
      setData((prev) => ({
        ...prev,
        result: {
          ats_score: data?.ats_score,
          resume: data?.tailored_resume_markdown,
        },
      }))
      setIsStreaming(false)
      setStep('result')
    },
    onSettled: () => {
      setIsStreaming(false)
    },
  })

  const handleAnalyze = React.useCallback(
    (resume?: string, job_description?: string) => {
      if (!resume || !job_description) return
      analyzeMutation.mutate({
        resume_text: resume,
        job_description: job_description,
      })
    },
    [analyzeMutation],
  )

  const handleTailor = React.useCallback(() => {
    if (
      !data.input.resume ||
      !data.input.job_description ||
      !data.analysis ||
      !data.analysis.ats_score ||
      !data.analysis.reasonings ||
      !data.analysis.improvements
    )
      return
    tailorMutation.mutate({
      resume_text: data.input.resume,
      job_description: data.input.job_description,
      ats_score: data.analysis.ats_score,
      reasonings: data.analysis.reasonings,
      improvements: data.analysis.improvements
        .filter((improv) => improv.checked)
        .map((improv) => improv.value),
      custom_user_request: data.analysis.custom_request?.enabled
        ? data.analysis.custom_request.value
        : undefined,
    })
  }, [tailorMutation, data.input, data.analysis])

  const handleFinish = React.useCallback(
    (
      action:
        | 're-analyze'
        | 'restart-with-new-resume'
        | 'restart-with-old-resume',
      file: FileData,
    ) => {
      if (!data?.result) return

      const finalResume = data.result?.resume || ''

      if (action === 're-analyze') {
        if (file) {
          setSelectedFiles([file])
        }

        setData((prev) => ({
          ...prev,
          input: {
            ...prev.input,
            resume: finalResume,
          },
          analysis: null,
        }))

        handleAnalyze(finalResume, data.input.job_description || '')
      } else if (action === 'restart-with-new-resume') {
        if (file) {
          setSelectedFiles([file])
        }
        setData((prev) => ({
          ...prev,
          input: {
            resume: finalResume,
            job_description: '',
          },
          analysis: null,
          result: null,
        }))
        setStep('upload')
      } else if (action === 'restart-with-old-resume') {
        setData((prev) => ({
          ...prev,
          input: {
            ...prev.input,
            job_description: '',
          },
          analysis: null,
          result: null,
        }))
        setStep('upload')
      }
    },
    [data],
  )

  return {
    state: {
      selectedFiles,
      data,
      step,
      isStreaming,
      isAnalyzing: analyzeMutation.isPending,
      isTailoring: tailorMutation.isPending,
    },
    actions: {
      setSelectedFiles,
      setData,
      setStep,
      handleAnalyze,
      handleTailor,
      handleFinish,
    },
  }
}
